// Landing page with pricing tiers and login form

import React, { useState, useEffect } from 'react';
import { Mail, Check, ArrowRight, Building2, Sparkles, Loader2 } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../services/api';
import { openNewCustomerCheckout } from '../services/paddle';
import type { PaddleConfig } from '../types';

const freeTierFeatures = [
  'Unlimited conversations & tokens (BYOK)',
  'Full 6-layer prompt stack',
  'Human-in-the-loop approvals',
  'Audit logging',
  'Direct API providers (OpenAI, Anthropic, Google)',
  'Per-role tool access control',
  'Declarative page context',
  'Admin UI',
  '1 knowledge base (TXT, MD, PDF)',
  '3 custom roles',
  '1 AI assistant',
  '1 MCP server connection',
];

const proTierFeatures = [
  'Cloud providers (AWS Bedrock, Azure, Vertex AI)',
  'Business intelligence & analytics',
  'File handling (upload, OCR, PDF)',
  'Playbooks (workflow guidance)',
  'Multiple model configurations',
  'Visual theming & white-labeling',
  'Admin API & programmatic access',
  'Health monitoring & usage dashboard',
  'Configurable data retention policies',
  '5 knowledge bases (+ DOCX, XLSX, CSV, HTML)',
  '5 admin users',
  '10 custom roles',
  '3 AI assistants',
  '3 MCP server connections',
];

const enterpriseTierFeatures = [
  'Air-gapped deployment (offline license)',
  'SSO / SAML integration',
  'Custom / self-hosted endpoints (Ollama, vLLM)',
  'Intelligent model routing',
  'Priority support with SLA',
  '20-30 hrs/year professional services',
  'Dedicated onboarding & roadmap influence',
  'Custom admin permissions & scoped API keys',
  'Custom legal terms & DPA',
  'Unlimited assistants, roles & MCP connections',
  'Unlimited knowledge bases (all file types)',
];

export function Landing() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [paddleConfig, setPaddleConfig] = useState<PaddleConfig | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    api.getPaddleConfig().then(setPaddleConfig).catch(() => {});
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    try {
      const result = await api.requestLogin(email);
      setMessage(result.message);
    } catch (err) {
      setError('Failed to send login link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProCheckout = () => {
    if (!paddleConfig) return;
    setCheckoutLoading(true);
    openNewCustomerCheckout(paddleConfig);
    setCheckoutLoading(false);
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="relative">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <p className="text-sm font-medium uppercase tracking-wider text-accent mb-4">
            Pricing
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Self-hosted on your infrastructure. Bring your own LLM API keys. No
            per-user, per-token, or per-connection fees.
          </p>
        </div>

        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
        {/* Free Tier */}
        <div className="rounded-xl border border-border bg-card p-8 flex flex-col">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-1">Free</h3>
            <p className="text-sm text-muted-foreground">Build a complete proof of concept</p>
          </div>

          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-foreground">$0</span>
              <span className="text-muted-foreground">/forever</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">No license required</p>
          </div>

          <a
            href="https://github.com/intrigsoft/dioschub"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 border border-border text-foreground font-medium px-4 py-2.5 rounded-md hover:bg-secondary transition-colors bg-transparent text-sm mb-8"
          >
            Get Started
            <ArrowRight className="h-4 w-4" />
          </a>

          <div className="border-t border-border pt-6 flex-1">
            <p className="text-sm font-medium text-foreground mb-4">Includes:</p>
            <ul className="space-y-3">
              {freeTierFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Pro Tier */}
        <div className="rounded-xl border-2 border-accent bg-card p-8 flex flex-col relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white">
              <Sparkles className="h-3 w-3" />
              Most Popular
            </span>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-1">Pro</h3>
            <p className="text-sm text-muted-foreground">Ship to production with confidence</p>
          </div>

          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-foreground">$500</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">$6,000/year, billed annually per instance</p>
          </div>

          <button
            onClick={handleProCheckout}
            disabled={!paddleConfig || checkoutLoading}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent text-white font-medium px-4 py-2.5 rounded-md hover:bg-accent/90 transition-all text-sm mb-8 disabled:opacity-50 disabled:pointer-events-none"
          >
            {checkoutLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Subscribe to Pro
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>

          <div className="border-t border-border pt-6 flex-1">
            <p className="text-sm font-medium text-foreground mb-4">Everything in Free, plus:</p>
            <ul className="space-y-3">
              {proTierFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Enterprise Tier */}
        <div className="rounded-xl border border-border bg-card p-8 flex flex-col">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-1">Enterprise</h3>
            <p className="text-sm text-muted-foreground">Full platform with dedicated support</p>
          </div>

          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-foreground">Custom</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Annual contract, sales-led</p>
          </div>

          <a
            href="https://outlook.office.com/book/DioschubDemo@intrigsoft.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 border border-border text-foreground font-medium px-4 py-2.5 rounded-md hover:bg-secondary transition-colors bg-transparent text-sm mb-8"
          >
            <Building2 className="h-4 w-4" />
            Contact Sales
          </a>

          <div className="border-t border-border pt-6 flex-1">
            <p className="text-sm font-medium text-foreground mb-4">Everything in Pro, plus:</p>
            <ul className="space-y-3">
              {enterpriseTierFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom note */}
      <div className="text-center mb-16">
        <p className="text-sm text-muted-foreground">
          All tiers include unlimited conversations, no token limits, audit logging, and full security controls.
          <br />
          You bring your own LLM API keys — your AI spend stays with your provider.
        </p>
      </div>

      {/* Login Section */}
      <div className="max-w-md mx-auto">
        <div className="bg-card border border-border rounded-xl p-8 hover:border-accent/50 transition-colors">
          <h2 className="text-xl font-bold mb-2 text-center text-foreground">Already a subscriber?</h2>
          <p className="text-muted-foreground mb-6 text-center text-sm">
            Enter your email to receive a secure login link to manage your licenses.
          </p>

          <form onSubmit={handleLogin}>
            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium text-accent uppercase tracking-wider mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-2.5 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground font-medium py-2.5 px-4 rounded-md hover:bg-primary/90 transition-all disabled:opacity-50 disabled:pointer-events-none text-sm"
            >
              {isLoading ? 'Sending...' : 'Send Login Link'}
            </button>
          </form>

          {message && (
            <div className="mt-5 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-sm text-emerald-400">{message}</p>
            </div>
          )}

          {error && (
            <div className="mt-5 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
